// TimezonePicker.tsx
import React, { useMemo, useState } from 'react';
import { useTimezoneStore } from '../model/store';
import { MAX_TIMEZONES } from '../config';
import { getTimezonesList } from '../lib/getTimezonesList';
import { EmptyState, Label, ModalTemplate } from '@/shared/ui';
import { MinusIcon, PlusIcon, SearchIcon } from '@/shared/assets';
import { twMerge } from 'tailwind-merge';
import { toast } from 'sonner';

const allTimezones = getTimezonesList()

const TimezonePicker: React.FC = () => {
    const { selected, addTimezone, removeTimezone } = useTimezoneStore();
    const [search, setSearch] = useState('');

    const selectedCodes = useMemo(
        () => new Set(selected.map((tz) => tz.tzCode)),
        [selected],
    );

    const canAddMore = selected.length < MAX_TIMEZONES;

    const filteredAndSorted = useMemo(() => {
        const query = search.trim().toLowerCase();

        const filtered = allTimezones.filter((tz) => {
            if (!query) return true;
            return (
                tz.name.toLowerCase().includes(query) ||
                tz.tzCode.toLowerCase().includes(query)
            );
        });

        // selected timezones go first
        return filtered.sort((a, b) => {
            const aSel = selectedCodes.has(a.tzCode);
            const bSel = selectedCodes.has(b.tzCode);
            if (aSel && !bSel) return -1;
            if (!aSel && bSel) return 1;
            return a.name.localeCompare(b.name);
        });
    }, [search, selectedCodes]);

    return (
        <div className="w-full px-5 flex flex-col">
            <div className="pb-5 pt-1">
                <Label className="relative flex h-fit w-full flex-col">
                    <SearchIcon className="absolute left-[6px] top-1/2 -translate-y-1/2" />
                    <input
                        className="rounded-xl bg-base-300 px-[6px] py-2 pl-8 text-base outline-none placeholder:text-base-100"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                        }}
                        placeholder='Search'
                    />
                </Label>
            </div>

            {/* List */}
            <ul className="max-h-[432px] space-y-1 overflow-y-auto pr-1">
                {filteredAndSorted.length ? filteredAndSorted.map((tz) => {
                    const isSelected = selectedCodes.has(tz.tzCode);
                    const isDisabled = !isSelected && !canAddMore;
                    const canNotDelete = isSelected && selectedCodes?.size === 1
                    const handleClick = () => {
                        if (isDisabled) {
                            toast.message(
                                `Maximum of 4 time zones allowed. 
                                Delete one to choose a new one.`
                            )
                        }
                        if (isSelected) {
                            removeTimezone(tz.tzCode);
                        } else if (canAddMore) {
                            addTimezone(tz);
                        }
                    };



                    return (
                        <li
                            key={tz.tzCode}
                            className="flex items-center justify-between rounded-[20px] px-2 h-[72px]"
                        >
                            <span className={twMerge("truncate typography-16r", ((!canAddMore && !isSelected) || canNotDelete) && 'text-base-300')}>{tz.name}</span>

                            <button
                                type="button"
                                onClick={handleClick}
                                disabled={canNotDelete}
                                className={twMerge(
                                    'flex h-10 w-10 items-center justify-center rounded-full text-xl font-semibold cursor-pointer',
                                    isSelected
                                        ? 'bg-contrast-500 text-contrast-500'
                                        : 'bg-base-300 text-base-500',
                                    isDisabled && 'text-base-300',
                                )}
                            >
                                {isSelected ? <MinusIcon className="w-5" /> : <PlusIcon className="w-5" />}
                            </button>
                        </li>
                    )
                }) : <EmptyState className="pb-5" />}
            </ul>
        </div>
    );
};


export const EditWorldTimeModal = () => {
    return <ModalTemplate
        id="edit-time-modal"
        title="World time"
        isHasCloseBtn
        description="Add up to 4 time zones"
        classNames={{
            wrapper: 'rounded-[32px]',
        }}
    >
        <TimezonePicker />
    </ModalTemplate>
}